import type { FlagPriority, FlagTypes } from '@generated/graphql';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { SegmentedControl } from '@ovComponents/2-component/segmentedControl/segmentedControl';
import { getReconFlagTypeLabel } from '@ovComponents/4-module/reconFlags/utils/reconFlags.config';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { useTranslation } from 'react-i18next';
import type {
  FlagPolicyEnabledState,
  FlagPolicyRowView,
} from './flagPolicies.types';
import { FlagPolicyConfigForm } from './flagPolicyConfigForm';

type FlagPolicyRowProps = {
  row: FlagPolicyRowView;
  disabled?: boolean;
  onEnabledChange: (flagType: FlagTypes, state: FlagPolicyEnabledState) => void;
  onPriorityChange: (flagType: FlagTypes, priority: FlagPriority) => void;
  onThresholdCentsChange: (flagType: FlagTypes, value: number | null) => void;
  onPercentThresholdChange: (flagType: FlagTypes, value: number | null) => void;
  testId?: string;
  underline?: boolean;
};

export const FlagPolicyRow = ({
  row,
  disabled = false,
  onEnabledChange,
  onPriorityChange,
  onThresholdCentsChange,
  onPercentThresholdChange,
  testId,
  underline = true,
}: FlagPolicyRowProps) => {
  const { t } = useTranslation([
    'settings',
    'shared',
    'components',
    'reconciliation',
  ]);
  const { sys } = useThemeTokens();

  const isOn = row.enabledControlState === 'ON';
  const isInherited = row.enabledControlState === 'UNDEFINED';
  const showConfigForm = isOn || isInherited;

  const showInheritedHint =
    isInherited && row.originalEnabledControlState === 'UNDEFINED';

  return (
    <Box
      display='flex'
      justifyContent='space-between'
      alignItems='flex-start'
      borderBottom={
        underline ? `1px solid ${sys.color.outlineVariant}` : 'none'
      }
      pb={3}
      pt={1}
      gap={2}
    >
      <Typography variant='labelLarge' pt={0.5}>
        {getReconFlagTypeLabel(t, row.flagType)}
      </Typography>
      <Box minWidth={{ xs: '100%', sm: '400px' }} flexShrink={0}>
        <SegmentedControl
          testId={testId}
          value={row.enabledControlState}
          onChange={(value) =>
            onEnabledChange(row.flagType, value as FlagPolicyEnabledState)
          }
          disabled={disabled}
          segments={[
            { label: t('shared:yes'), value: 'ON' },
            { label: t('shared:no'), value: 'OFF' },
            { label: t('shared:inherited'), value: 'UNDEFINED' },
          ]}
        />
        {showInheritedHint && (
          <Typography
            display='flex'
            alignItems='center'
            gap={0.5}
            mt={0.5}
            variant='bodySmall'
            colorVariant='variant'
          >
            <InfoOutlined sx={{ fontSize: '12px', verticalAlign: 'bottom' }} />
            {t('components:featureFlags.inheritedSetTo')}{' '}
            {t(`components:featureFlags.${row.effective.enabled}`)}
          </Typography>
        )}
        {showConfigForm && (
          <FlagPolicyConfigForm
            row={row}
            readOnly={isInherited}
            disabled={disabled}
            onPriorityChange={onPriorityChange}
            onThresholdCentsChange={onThresholdCentsChange}
            onPercentThresholdChange={onPercentThresholdChange}
            testIdPrefix={testId}
          />
        )}
      </Box>
    </Box>
  );
};
